import React, { useEffect } from "react"
import { Elements } from "@stripe/react-stripe-js"
import { PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js"
import { useState } from "react"
import Modal from "aldoo-ra/Modal"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import Value from "aldoo-ra/Value"
import AldooStoreAPI from "aldoo-ra/Store/api"
import ToggleSwitch from "aldoo-ra/ToggleSwitch"
import { getStripePromise } from "aldoo-ra/Store/stripe-client"
import { store } from "aldoo-ra/Store"

const stripePromise = getStripePromise()
let setupLoading = false
let setupIntentCache = null

const themeColor = (varName) => {
  if (typeof window === "undefined") return undefined
  const value = getComputedStyle(document.documentElement).getPropertyValue(varName).trim()
  return value ? `rgb(${value})` : undefined
}

const PaymentMethodForm = ({ onSuccess, onCancel, clearSetupIntent }) => {
  const stripe = useStripe()
  const elements = useElements()
  const [loading, setLoading] = useState(false)
  const [ready, setReady] = useState(false)
  const [makeDefault, setMakeDefault] = useState(false)
  const [error, setError] = useState(null)

  const texts = TextBundle({ id: "aldoo-store" })

  const isReady = stripe && ready && !loading

  const handleSubmit = async (event) => {
    event.preventDefault()

    if (loading) return
    if (!stripe || !elements) return

    setLoading(true)
    setError(null)

    try {
      const { error: submitError } = await elements.submit()
      if (submitError) {
        throw submitError
      }

      const { error: confirmError, setupIntent } = await stripe.confirmSetup({
        elements,
        redirect: "if_required",
        confirmParams: {
          return_url: `${window.location.origin}/payment-result`,
        },
      })

      if (confirmError) {
        throw confirmError
      }

      const { setupIntent: setupIntentDetails } =
        await stripe.retrieveSetupIntent(setupIntent.client_secret)

      if (!setupIntentDetails || !setupIntentDetails.payment_method) {
        throw new Error(texts?.payment_method_retrieval_fail || "Failed to retrieve payment method details.")
      }

      const paymentMethodId = setupIntentDetails.payment_method

      const response = await AldooStoreAPI.attachPaymentMethod({
        payment_method_id: paymentMethodId,
        makeDefault,
      })

      if (response.error) {
        throw new Error(response.error)
      }

      clearSetupIntent?.()
      window.dispatchEvent(new CustomEvent("payment-methods-updated"))
      onSuccess?.()
    } catch (err) {
      setError(err.message)
      store.logPaymentError(err)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="w-full max-w-lg">
      {error && (
        <div className="mb-4 p-3 bg-error/10 border border-error/30 text-error rounded-md">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="mt-2 mb-0 min-h-[430px]">
          <PaymentElement
            onReady={() => setReady(true)}
            options={{
              layout: {
                type: "tabs",
                defaultCollapsed: false,
              },
            }}
          />
        </div>

        <div className="flex items-center space-x-3 my-6">
          <ToggleSwitch isOn={makeDefault} onChange={setMakeDefault} />
          <Typography variant="txt1" className="text-text my-0">
            {texts?.make_payment_method_default || "Make this my default payment method"}
          </Typography>
        </div>

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
          {onCancel && (
            <Button variant="secondary" onClick={onCancel}>
              {texts?.cta_add_payment_method_cancel || "Cancel"}
            </Button>
          )}

          <Button
            type="submit"
            disabled={!isReady || loading}
            variant="primary"
            className={`${isReady && !loading ? "bg-primary hover:bg-primary_hov" : "bg-gray_3 cursor-not-allowed"} transition-colors`}
          >
            {loading ? (
              <span className="flex items-center space-x-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <span>{texts?.processing_add_payment_method || "Processing..."}</span>
              </span>
            ) : (
              texts?.cta_add_payment_method || "Add Payment Method"
            )}
          </Button>
        </div>
      </form>
    </div>
  )
}

const AddPaymentMethod = ({ isOpen, onClose }) => {
  const texts = TextBundle({ id: "aldoo-store" })
  const [setupIntent, setSetupIntent] = Value({
    key: "payment-setup-intent",
    defaultValue: null,
  })
  const { getFragment } = store

  const PreparingPayment = getFragment("checkout-preparing-payment")
  const PaymentContainer = getFragment("checkout-container")

  useEffect(() => {
    const initializeSetup = async () => {
      if (setupIntent || setupIntentCache || setupLoading) return
      setupLoading = true

      try {
        const response = await AldooStoreAPI.initializePaymentMethodSetup()

        if (response.error) {
          console.error("Failed to initialize setup:", response.error)
          setupLoading = false
          return
        }

        setupIntentCache = response
        setupLoading = false
        setSetupIntent(response)
      } catch (error) {
        console.error("Failed to initialize setup:", error)
        setupLoading = false
      }
    }

    if (isOpen) initializeSetup()
  }, [isOpen])

  if (!isOpen) return null

  return (
    <Modal
      title={setupIntent && texts?.addPaymentMethod}
      isOpen={isOpen}
      variant="centered"
      className="md:max-w-[600px] min-h-[500px]"
      onClose={onClose}
      styleOverrides={{
        container: "w-full md:w-[755px] h-full md:h-auto md:max-h-[calc(100vh-1rem)] bg-paper overflow-y-auto md:rounded-[24px] pt-6 pb-6",
        closeButton: "absolute right-4 md:right-5 top-2 z-10",
        header: "relative flex justify-between items-center p-0 md:p-5",
        title: "flex-1 text-xl font-bold text-text md:mr-8 mt-0",
      }}
    >
      {!setupIntent && <PreparingPayment />}
      {setupIntent && setupIntent.clientSecret && (
        <PaymentContainer store={store}>
          <div className="flex justify-center px-6">
          <Elements
            stripe={stripePromise}
            options={{
              clientSecret: setupIntent.clientSecret,
              loader: "always",
              appearance: {
                variables: {
                  colorDanger: themeColor("--color-error"),
                  colorBackground: themeColor("--color-paper"),
                  colorText: themeColor("--color-text"),
                },
                rules: {
                  ".Label": {
                    color: themeColor("--color-text_secondary"),
                  },
                  ".TermsText": {
                    color: themeColor("--color-text_secondary"),
                  },
                },
              },
            }}
          >
            <PaymentMethodForm
              clearSetupIntent={() => { setupIntentCache = null; setSetupIntent(null) }}
              onSuccess={() => {
                setupIntentCache = null
                onClose()
                setSetupIntent(null)
              }}
              onCancel={onClose}
            />
          </Elements>
          </div>
        </PaymentContainer>
      )}
    </Modal>
  )
}

export default AddPaymentMethod
